import { getFeatureFlags } from "@client-shared/services/api/featureFlags";
import type { FeatureFlags } from "@shared/types";
import { useQuery } from "@tanstack/react-query";

export const FEATURE_FLAGS_QUERY_KEY = "featureFlags";

const STALE_TIME_MS = 5 * 60_000;

interface FeatureFlagsResult {
  readonly flags: FeatureFlags;
  readonly isFeatureFlagsLoading: boolean;
}

/**
 * Resolves BCC feature flags for the vendor's business unit. Disabled when there's no
 * vendorRegion yet or until auth is ready (the vendor is restored from cache before login finishes, and an
 * early request has no Authorization header). On any failure (or before a vendor's flags exist in Salesforce) this
 * resolves to `{}`, so callers always get a usable object and every flag reads as fail-closed.
 */
export function useFeatureFlags(vendorRegion: string | undefined, countryCode?: string, isAuthReady = true): FeatureFlagsResult {
  const enabled = Boolean(vendorRegion) && isAuthReady;

  const { data, isLoading } = useQuery({
    queryKey: [FEATURE_FLAGS_QUERY_KEY, vendorRegion, countryCode],
    queryFn: () => {
      if (!vendorRegion) throw new Error("vendorRegion is required");
      return getFeatureFlags(vendorRegion, countryCode);
    },
    enabled,
    staleTime: STALE_TIME_MS,
    retry: 0,
    refetchOnWindowFocus: false,
  });

  // A disabled query keeps its cached data (e.g. after the auth token is removed), so mask it to stay fail-closed
  return { flags: enabled ? (data ?? {}) : {}, isFeatureFlagsLoading: enabled && isLoading };
}
